const MODEL_V = 36;
const cache = {};
let metaPromise;

const getMeta = () => (metaPromise ||= fetch(`/models/meta.json?v=${MODEL_V}`).then((r) => r.json()));

const hairCache = {};
export async function loadHair(sex) {
  if (hairCache[sex]) return hairCache[sex];
  const meta = (await (await fetch(`/models/hair.json?v=${MODEL_V}`)).json())[sex];
  const buf = await (await fetch(`/models/hair_${sex}.bin?v=${MODEL_V}`)).arrayBuffer();
  const T = { float32: Float32Array, uint32: Uint32Array };
  const arr = {};
  Object.entries(meta).forEach(([k, v]) => { if (Array.isArray(v)) arr[k] = new T[v[2]](buf, v[0], v[1]); });
  hairCache[sex] = { meta, arr };
  return hairCache[sex];
}

export async function loadModel(sex) {
  if (cache[sex]) return cache[sex];
  const meta = (await getMeta())[sex];
  const buf = await (await fetch(`/models/${sex}.bin?v=${MODEL_V}`)).arrayBuffer();
  const T = { float32: Float32Array, uint32: Uint32Array, uint8: Uint8Array };
  const arr = {};
  Object.entries(meta.layout).forEach(([k, [off, n, type]]) => { arr[k] = new T[type](buf, off, n); });
  cache[sex] = { meta, arr };
  return cache[sex];
}

export const weights = (age) => {
  if (age <= 11) return { child: 1, young: 0, old: 0 };
  if (age <= 25) { const t = (age - 11) / 14; return { child: 1 - t, young: t, old: 0 }; }
  const t = Math.max(0, Math.min(1, (age - 40) / 50));
  return { child: 0, young: 1 - t, old: t };
};

export function blend({ meta, arr }, age, prefix) {
  const w = weights(age);
  const floor = w.child * meta.floor.child + w.young * meta.floor.young + w.old * meta.floor.old;
  const src = ["child", "young", "old"].filter((k) => w[k] > 0);
  const out = new Float32Array(arr[`${prefix}_young`].length);
  for (let i = 0; i < out.length; i++) {
    let v = 0;
    for (const k of src) v += w[k] * arr[`${prefix}_${k}`][i];
    out[i] = i % 3 === 1 ? v - floor : v;
  }
  return out;
}

export function blendJoints({ meta }, age) {
  const w = weights(age);
  const J = {};
  Object.keys(meta.joints.young).forEach((k) => {
    J[k] = [0, 1, 2].map((i) => w.child * meta.joints.child[k][i] + w.young * meta.joints.young[k][i] + w.old * meta.joints.old[k][i]);
  });
  return J;
}

export const bodyHeight = ({ meta }, age) => {
  const w = weights(age);
  return w.child * meta.height.child + w.young * meta.height.young + w.old * meta.height.old;
};

// Surface hotspot directions per sub-zone: [outward(x by side), up, front]
export const SUB_DIRS = {
  penis: [0, 0.4, 1], glans: [0, -1, 0.8], scrotum: [0.4, -0.5, 0], perineum: [0, -1, -0.6], vulva: [0, 0.2, 1], vaginal_area: [0, -1, 0.3],
  forehead: [0, 0.5, 1], temples: [1, 0.4, 0.5], top_head: [0, 1, 0], back_head: [0, 0.1, -1], eyes: [0.35, 0.1, 1], face_jaw: [0.5, -0.9, 0.6], ears: [1, 0, -0.1],
  throat: [0, 0, 1], neck_side: [1, 0, 0], nape: [0, 0, -1],
  sternum: [0, 0.2, 1], chest_left: [0.8, 0.1, 0.6], chest_right: [-0.8, 0.1, 0.6], ribs: [1, -0.7, 0.3], breast: [0.45, -0.1, 1],
  between_blades: [0, 0.3, -1], shoulder_blade: [0.8, 0.4, -0.7], thoracic_spine: [0, -0.6, -1],
  epigastric: [0, 1, 0.6], ruq: [-0.8, 0.6, 0.6], luq: [0.8, 0.6, 0.6], navel: [0, 0, 1], rlq: [-0.8, -0.6, 0.6], llq: [0.8, -0.6, 0.6],
  lumbar: [0, 0, -1], flank: [1, 0, -0.3], sacrum: [0, -1, -0.6],
  groin: [0.9, -0.4, 0.6], pubic: [0, 0.5, 1], genital: [0, -1, 0.9],
  buttock: [0.6, 0, -1], tailbone: [0, 0.7, -1], sciatic: [0.7, -0.8, -0.6],
  sh_front: [0, 0, 1], sh_side: [1, 0.3, 0], sh_back: [0, 0, -1], sh_joint: [0, 1, 0],
  biceps: [0, 0.3, 1], triceps: [0, -0.3, -1],
  el_inner: [0, 0, 1], el_outer: [0, 0.8, -0.4], el_tip: [0, -0.3, -1],
  fa_front: [0, -0.3, 1], fa_back: [0, 0.6, -0.5],
  wrist: [-1, 0, 0], palm: [0, -1, 0.3], hand_back: [0, 1, 0], fingers: [1, 0, 0], thumb: [0, 0, 1],
  hip_side: [1, 0, 0], hip_joint: [0.4, 0.6, 0.4], hip_front: [0, 0, 1],
  quad: [0, 0, 1], th_inner: [-1, 0, 0], th_outer: [1, 0, 0], hamstring: [0, 0, -1],
  kneecap: [0, 0, 1], kn_inner: [-1, 0, 0.2], kn_outer: [1, 0, 0.2], kn_back: [0, 0, -1],
  shin: [0, 0.3, 1], calf: [0, 0.3, -1], achilles: [0, -1, -1],
  ankle: [1, 0.7, 0], heel: [0, -0.3, -1], sole: [0, -1, 0], foot_top: [0, 1, 0.5], toes: [0, 0, 1],
};

export const BACK_ZONES = ["upper_back", "lower_back", "glutes"];
